import {
  Component,
  ElementRef,
  inject,
  input,
  output,
  linkedSignal,
  computed,
  effect,
  untracked,
  InputSignal,
  OutputEmitterRef,
  Signal
} from '@angular/core';

import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { PageNoScrollStrategy } from '../../utils/page-scroll/page-no-scroll-strategy';
import { PAGE_SCROLL_STRATEGY } from '../../utils/page-scroll/page-scroll-strategy';
import { IconComponent } from '../icon/icon.component';
import { PaginationService } from './pagination.service';

import type { PageScrollStrategy } from '../../utils/page-scroll/page-scroll-strategy';

@Component({
  selector: 'td-wb-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  imports: [IconComponent, TranslateDirective, TranslatePipe]
})
export class PaginationComponent {
  private static readonly SELECTED_BUTTON_CLASS: string = 'td-wb-pagination__list-item--selected';

  /**
   * The number of items that will be paginated. This number helps establish the number of pages that need to be shown.
   */
  itemCount: InputSignal<number> = input.required<number>();

  /**
   * The number of items to show per page.
   */
  itemsPerPage: InputSignal<number> = input.required<number>();

  /**
   * The number of pages that should be grouped together. Specifying a number above 1 allows for multiple selected pages.
   * This is currently only used in the Watchlist modal.
   * Defaults to 1, meaning only 1 page is selected at a time.
   */
  groupSize: InputSignal<number> = input<number>(1);

  /**
   * Whether the arrows should be shown for the next and previous pages.
   * Defaults to true.
   */
  hideArrows: InputSignal<boolean> = input<boolean>(false);

  /**
   * Whether changing pages is disabled.
   * Defaults to false. If true, prevents page changes and should show the disabled state for arrows, if shown.
   */
  disabled: InputSignal<boolean> = input<boolean>(false);

  /**
   * The currently selected page. Defaults to 0, which is the first page.
   * The pagination component follows array syntax for pages, so selecting the 4th page requires passing 3.
   */
  readonly selectedPageInput: InputSignal<number> = input<number>(0, {
    alias: 'selectedPage'
  });

  readonly selectedPageChange: OutputEmitterRef<number> = output<number>();

  readonly selectedPage = linkedSignal<number>(() => this.getClampedPage(this.selectedPageInput()));

  /**
   * The maximum continuous page links shown. If not set, then all page links are shown.
   */
  maxShownPageNumberLinks: InputSignal<number | undefined> = input<number | undefined>(5);

  /**
   * Set to true if you want the pagination component will stay in the visual viewport on page change
   * Note: Use only for client side pagination
   */
  scrollIntoViewOnPageChange: InputSignal<boolean> = input<boolean>(false);

  /**
   * This is the EventEmitter that is triggered when a new page is selected.
   * It emits an array containing the group of pages that are selected. It is only used/emitted when groupSize is greater than 1.
   */
  selectedGroupChange: OutputEmitterRef<Array<number>> = output<Array<number>>();

  /**
   * The number of pages that are shown.
   */
  numberOfPages: Signal<number> = computed<number>(() => {
    const count = Math.ceil(this.itemCount() / this.itemsPerPage());
    return Math.max(count, 1);
  });

  pages: Signal<Array<number | undefined>> = computed<Array<number | undefined>>(() =>
    this.paginationService.generatePageRange(this.selectedPage(), this.numberOfPages(), this.maxShownPageNumberLinks())
  );

  private readonly elementRef = inject(ElementRef);
  private readonly paginationService = inject(PaginationService);
  private readonly pageScrollStrategy: PageScrollStrategy = inject(PAGE_SCROLL_STRATEGY, { optional: true }) ?? new PageNoScrollStrategy();

  constructor() {
    // effect: calls PageScrollStrategy.scroll() when selectedPage changes —
    // scroll() is an imperative browser/DOM API injected via InjectionToken;
    // untracked prevents the scroll side effect from re-entering signal tracking.
    effect(() => {
      this.selectedPage();
      untracked(() => {
        this.pageScrollStrategy.scroll();
      });
    });

  }

  /**
   * This is the function that is called when the page is changed by either clicking on a specific page number,
   * or by clicking on the next or previous buttons.
   *
   * @param index The page number to change to.
   */
  onPageClick(index: number): void {
    if (!this.disabled()) {
      const previousPage = this.selectedPage();
      this.setSelectedPage(index);
      const currentPage = this.selectedPage();
      if (this.groupSize() > 1) {
        const indexArray = Array.from({ length: this.groupSize() }, (_v: number, k: number): number => k + currentPage);
        this.selectedGroupChange.emit(indexArray);
      }
      this.scrollIntoView(previousPage, currentPage);
    }
  }

  /**
   * This function is used to return all the appropriate classes for each page number based on their position.
   *
   * @param index
   * @returns
   */
  getPageSelectorButtonClass(index: number): Array<string> {
    const classes = ['td-wb-pagination__list-item'];
    const baseClass = PaginationComponent.SELECTED_BUTTON_CLASS;
    const lastPageInGroup = this.selectedPage() + this.groupSize() - 1;

    if (this.groupSize() === 1 && index === this.selectedPage()) {
      classes.push(baseClass, `${baseClass}-single`);
    } else if (this.groupSize() > 1) {
      if (index === this.selectedPage()) {
        classes.push(baseClass, `${baseClass}-first`);
      } else if (index > this.selectedPage() && index < lastPageInGroup) {
        classes.push(baseClass);
      } else if (index === lastPageInGroup) {
        classes.push(baseClass, `${baseClass}-last`);
      }
    }
    return classes;
  }

  /**
   * Evaluates whether the selected page is the first page or not.
   *
   * @returns True if the selected page is the first page, false otherwise.
   */
  isFirstPage(): boolean {
    return this.selectedPage() === 0;
  }

  /**
   * Evaluates whether the selected page is equal to or greater than the last page or not.
   *
   * @returns True if the selected page is equal to or greater than the last page, false otherwise.
   */
  isLastPage(): boolean {
    return this.selectedPage() >= this.getLastPageNumber();
  }

  /**
   * Sets the selected page equal to the passed in index unless it is beyond the range of possible options.
   *
   * @param index
   */
  private setSelectedPage(index: number): void {
    if (!this.disabled() || this.groupSize() > 1) {
      const nextPage = this.getClampedPage(index);
      if (nextPage !== this.selectedPage()) {
        this.selectedPage.set(nextPage);
        this.selectedPageChange.emit(nextPage);
      }
    }
  }

  /**
   * This function returns the last page number based on the number of pages and the group size.
   *
   * @returns
   */
  private getLastPageNumber(): number {
    return this.numberOfPages() - this.groupSize();
  }

  private getClampedPage(index: number): number {
    const lastPage = Math.max(this.getLastPageNumber(), 0);
    return Math.max(Math.min(index, lastPage), 0);
  }

  private scrollIntoView(previousPage: number, currentPage: number): void {
    if (this.scrollIntoViewOnPageChange() &&
      (previousPage === this.getLastPageNumber() || currentPage === this.getLastPageNumber())) {
      setTimeout((): void => {
        if (!this.isInViewport(this.elementRef.nativeElement)) {
          this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
      });
    }
  }

  private isInViewport(elem: HTMLElement): boolean {
    const bounding = elem.getBoundingClientRect();
    return bounding.top >= 150 && bounding.left >= 0 && bounding.bottom <= window.innerHeight && bounding.right <= window.innerWidth;
  }
}
